import type { ContentItem as BaseContentItem } from "./CoursePreviewPage";
import {
    cn,
    IconBadge,
    ListCardRow,
    Text,
    Mono,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@athena/ui";
import { Pencil, EyeOff, Trash, Eye, GripVertical } from "lucide-react";
import type { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import { CONTENT_TYPE_META } from "./CourseHelpers";

type ContentItem = BaseContentItem;

function contentSubtitle(content: ContentItem): string {
    switch (content.__typename) {
        case "VideoContent":
            return "Video";
        case "TextContent":
            return "Reading";
        case "QuizContent":
            return `Quiz · ${content.questions.length} questions`;
        default:
            return "";
    }
}

type ContentRowProps = ContentItem & {
    onEdit: () => void;
    onToggleVisibility: () => void;
    isActive: boolean;
    setNodeRef?: (el: HTMLElement | null) => void;
    listeners?: DraggableSyntheticListeners;
    attributes?: DraggableAttributes;
    isDragging?: boolean;
    onHandleDelete: () => void;
    onClick?: () => void;
};

const ContentRow = ({
    onEdit,
    onToggleVisibility,
    isActive,
    setNodeRef,
    listeners,
    attributes,
    isDragging,
    onHandleDelete,
    onClick,
    ...content
}: ContentRowProps) => {
    return (
        <ListCardRow
            ref={setNodeRef}
            className={cn(
                "group hover:bg-card-background rounded-lg hover:cursor-pointer",
                content.status === "HIDDEN" && "opacity-50",
                isActive && "animate-[highlight-ring_1.5s_ease-out_forwards]",
                isDragging && "opacity-40",
            )}
            onClick={onClick}
        >
            <button className="cursor-grab touch-none" {...attributes} {...listeners}>
                <GripVertical className="text-border h-4 w-4 shrink-0" />
            </button>
            <IconBadge icon={CONTENT_TYPE_META[content.contentType].icon} variant={content.contentType} />
            <div className="flex-1">
                <Text weight="semibold">{content.title}</Text>
                <Mono muted size="xs">
                    {contentSubtitle(content as ContentItem)}
                    {content.status === "HIDDEN" && " · hidden"}
                </Mono>
            </div>
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggleVisibility();
                                    }}
                                    aria-label={content.status === "HIDDEN" ? "Show" : "Hide"}
                                    className="hover:bg-border rounded-md p-1.5"
                                >
                                    {content.status === "HIDDEN" ? (
                                        <Eye className="text-muted-foreground h-4 w-4" />
                                    ) : (
                                        <EyeOff className="text-muted-foreground h-4 w-4" />
                                    )}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-card text-xs">
                                {content.status === "HIDDEN" ? "Show" : "Hide"}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onHandleDelete();
                                    }}
                                    aria-label="Delete"
                                    className="hover:bg-border rounded-md p-1.5"
                                >
                                    <Trash className="text-muted-foreground h-4 w-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-card text-xs">{"Delete"}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                    }}
                    aria-label="Edit"
                    className="group/edit hover:border-secondary flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
                >
                    <Pencil className="text-muted-foreground group-hover/edit:text-secondary h-3.25 w-3.25" />
                </button>
            </div>
        </ListCardRow>
    );
};

export default ContentRow;
